import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import PreferenceButton from '../PreferenceButton'
import GlobalPreference from '../GlobalPreference'
import React, { useEffect, useState, useRef } from 'react'
import { useBaseStyles } from '../../../hooks/styles/useBaseStyles'
import { useConfigComponentsStyles } from '../../../hooks/styles/useConfigComponentsStyles'
import {
  IconButton,
  Button,
  Collapsible,
  CircularProgress
} from '../../../components'
import { useTheme } from '../../../index'
import { useNotificationsHomeContext } from '../../context'

import MemoArrowBackIosNew from '../Icons/ArrowBackIosNew'
import MemoHourglassEmpty from '../Icons/HourglassEmpty'

export const PanelHeader = () => {
  const theme = useTheme()

  const { CollapsibleWrapper } = useBaseStyles()
  const {
    PanelHeaderContainer,
    PanelHeaderContent,
    PanelHeaderTitle,
    UnsavedChangesContainer,
    UnsavedChangesText,
    UnsavedChangesButtonContainer,
    UnsavedChangesNoButton,
    UnsavedChangesYesButton,
    CollapsibleStyle
  } = useConfigComponentsStyles()

  const {
    data: {
      userPreference,
      socketInstance,
      openConfigUnsaved,
      resetPreference,
      updatedPreference,
      globalChannelPreference,
      resetGlobalChannelPreference
    },
    handlers: {
      setShowConfig,
      setUserPreference,
      setOpenConfigUnsaved,
      setGlobalChannelPreference
    }
  } = useNotificationsHomeContext()

  return (
    <React.Fragment>
      <PanelHeaderContainer openConfigUnsaved={openConfigUnsaved}>
        <PanelHeaderContent>
          <IconButton
            onClick={(e) => {
              // Check if preferences have unsaved changes before closing the panel
              if (
                !isEqual(userPreference, resetPreference) ||
                !isEqual(globalChannelPreference, resetGlobalChannelPreference)
              ) {
                setOpenConfigUnsaved(true)
              } else {
                setShowConfig(false)
              }
            }}
          >
            <MemoArrowBackIosNew
              fontSize='small'
              color={theme.palette.text.secondary}
            />
          </IconButton>
          <PanelHeaderTitle>Notification Preferences</PanelHeaderTitle>
        </PanelHeaderContent>
        <PreferenceSaveButton variant='contained' />
      </PanelHeaderContainer>

      <CollapsibleWrapper>
        <Collapsible
          in={openConfigUnsaved}
          style={CollapsibleStyle(openConfigUnsaved)}
        >
          <UnsavedChangesContainer>
            <UnsavedChangesText>
              Do you want to save the changes made?
            </UnsavedChangesText>

            <UnsavedChangesButtonContainer>
              <UnsavedChangesNoButton
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setOpenConfigUnsaved(false)
                  setUserPreference(cloneDeep(resetPreference))
                  setGlobalChannelPreference(
                    cloneDeep(resetGlobalChannelPreference)
                  )
                  setShowConfig(false)
                }}
              >
                No
              </UnsavedChangesNoButton>

              <UnsavedChangesYesButton
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  socketInstance.emit('set:preference', updatedPreference)
                }}
              >
                Yes
              </UnsavedChangesYesButton>
            </UnsavedChangesButtonContainer>
          </UnsavedChangesContainer>
        </Collapsible>
      </CollapsibleWrapper>
    </React.Fragment>
  )
}

export const PanelBody = React.memo((props) => {
  const theme = useTheme()
  const {
    PanelBodyContainer,
    PanelBodyContent,
    PreferenceSection,
    PreferenceSectionTitle,
    PreferenceSectionContent,
    PreferenceTopicContainer,
    PreferenceTopicHeader,
    PreferenceTopicName,
    PreferenceChannelContainer,
    PreferenceFillerDiv,
    EmptyStateContainer,
    EmptyStateText
  } = useConfigComponentsStyles()

  const {
    data: { userPreference, openConfigUnsaved, globalChannelPreference }
  } = useNotificationsHomeContext()

  // Get all sections from user preferences - memoize to prevent recreating array
  const sectionsArr = React.useMemo(
    () => Object.keys(userPreference.result),
    [userPreference.result]
  )

  return (
    <PanelBodyContainer
      sectionsArr={sectionsArr}
      className='fyno-user-preferences-container'
    >
      <PanelBodyContent>
        {globalChannelPreference &&
          Object.keys(globalChannelPreference).length > 0 && (
            <GlobalPreference channels={Object.keys(globalChannelPreference)} />
          )}

        {sectionsArr.length > 0 ? (
          <div>
            {sectionsArr.map((section, index) => (
              <PreferenceSection
                key={section + index}
                className='preference-section'
              >
                <PreferenceSectionTitle>{section}</PreferenceSectionTitle>

                <PreferenceSectionContent>
                  {userPreference.result[section].map((topic, index) => (
                    <PreferenceTopicContainer
                      isLast={
                        index === userPreference.result[section].length - 1
                      }
                      key={topic.subscription_id}
                    >
                      <PreferenceTopicHeader>
                        <PreferenceTopicName>{topic.name}</PreferenceTopicName>
                      </PreferenceTopicHeader>
                      <PreferenceChannelContainer>
                        {Object.entries(topic.preference).map(
                          (channel) =>
                            channel[1] === 'required' && (
                              <PreferenceButton
                                key={channel}
                                section={section}
                                topic={topic}
                                tooltip={true}
                                type={channel[1]}
                                label={channel[0]}
                              />
                            )
                        )}
                        {Object.entries(topic.preference).map((channel) => {
                          return (
                            channel[1] !== 'required' && (
                              <PreferenceButton
                                key={channel[0]}
                                section={section}
                                topic={topic}
                                tooltip={
                                  globalChannelPreference[channel[0]] || false
                                }
                                type={
                                  globalChannelPreference[channel[0]] &&
                                  globalChannelPreference[channel[0]] === true
                                    ? 'opted-out'
                                    : channel[1]
                                }
                                label={channel[0]}
                              />
                            )
                          )
                        })}
                      </PreferenceChannelContainer>
                    </PreferenceTopicContainer>
                  ))}
                </PreferenceSectionContent>
              </PreferenceSection>
            ))}

            {/* Filler */}
            <PreferenceFillerDiv />
          </div>
        ) : (
          <EmptyStateContainer>
            <MemoHourglassEmpty fontSize='large' color='secondary' />
            <EmptyStateText>
              No preferences available at the moment
            </EmptyStateText>
          </EmptyStateContainer>
        )}
      </PanelBodyContent>
    </PanelBodyContainer>
  )
})
PanelBody.displayName = 'PanelBody'

export const PreferenceSaveButton = React.memo((props) => {
  const { SaveButtonWrapper } = useConfigComponentsStyles()

  const {
    data: {
      socketInstance,
      updatedPreference,
      globalChannelPreference,
      resetGlobalChannelPreference,
      userPreference,
      resetPreference
    }
  } = useNotificationsHomeContext()
  const [preferenceUpdated, setPreferenceUpdated] = useState(false)
  const buttonRef = useRef()
  const theme = useTheme()

  useEffect(() => {
    const timer = setTimeout(() => {
      setPreferenceUpdated(false)
    }, 1000)
    return () => clearTimeout(timer)
  }, [preferenceUpdated])

  const handleSavePreference = React.useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    socketInstance.emit('set:preference', updatedPreference)
  }, [socketInstance, updatedPreference])

  const hasChanges = React.useMemo(
    () =>
      !isEqual(userPreference, resetPreference) ||
      !isEqual(globalChannelPreference, resetGlobalChannelPreference),
    [userPreference, resetPreference, globalChannelPreference, resetGlobalChannelPreference]
  )

  return hasChanges ? (
    <SaveButtonWrapper>
      <Button
        variant={props?.variant || 'text'}
        ref={buttonRef}
        size='small'
        // disabled={preferenceUpdated || openConfigUnsaved || props.disabled}
        onClick={handleSavePreference}
      >
        Save
      </Button>
      {preferenceUpdated && (
        <CircularProgress
          size={24}
          sx={{
            color: 'success',
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-12px',
            marginLeft: '-12px'
          }}
        />
      )}
    </SaveButtonWrapper>
  ) : null
})
PreferenceSaveButton.displayName = 'PreferenceSaveButton'

export default { PanelBody, PanelHeader }
